import { useQuery, type UseQueryOptions } from "@tanstack/react-query";

import { packNetAxios } from "@/lib/api-client";
import { baseRoutes } from "@/api/base-routes";
import { type AuthenticationMethod, authenticationMethodSchema } from "@/schemas/authentication-method";

export const TENANT_AUTHENTICATION_METHOD_QUERY_KEY = ["tenant-authentication-method"] as const;

/**
 * Fetch tenant authentication method configuration from the identity API
 * Returns information about whether the tenant uses SAML, OIDC, Password, or Mixed authentication
 */
const fetchTenantAuthenticationMethod = async (): Promise<AuthenticationMethod> => {
	const response = await packNetAxios.get<AuthenticationMethod>(`${baseRoutes.identity}/v1/tenants/authenticationType`);

	return authenticationMethodSchema.parse(response.data);
};

/**
 * Type for additional query options
 */
type UseAuthenticationMethodOptions = Omit<
	UseQueryOptions<AuthenticationMethod, Error, AuthenticationMethod, readonly string[]>,
	"queryKey" | "queryFn"
>;

/**
 * React Query hook for fetching tenant authentication method configuration
 *
 * @param queryConfig - Optional React Query configuration
 * @returns Object with tenant authentication method data, loading state, error, and refetch function
 *
 * @example
 * const { data: authMethod, isLoading, error } = useTenantAuthenticationMethod();
 *
 * if (isLoading) return <Loader />;
 * if (error) return <Error />;
 *
 * // Hide button if authentication method is SAML
 * const shouldHideButton = authMethod?.name === "Saml";
 */
export const useAuthenticationMethod = (queryConfig: UseAuthenticationMethodOptions = {}) => {
	return useQuery({
		queryKey: TENANT_AUTHENTICATION_METHOD_QUERY_KEY,
		queryFn: fetchTenantAuthenticationMethod,
		...queryConfig,
	});
};
